import { put, takeEvery, call, select } from "redux-saga/effects";
import { getAccessToken, redirectToLoginPage } from "../utils/auth-provider";
import { RootState } from "../redux/reducers/rootReducer";
import { getCurrentSystemKey, buildPortalUrl, isGohHost } from "../config/domain";

// Watcher: listen to specific action and call worker
export function* watchHttpAction() {
  yield takeEvery("*", sendHttpRequest);
}

// Worker: send HTTP request
export function* sendHttpRequest(action: any): Generator<any, void, any> {
  if ("http" in action && "cognito" in action.http) {
    yield call(cognitoRequestSender, action);
  }
}

// Selectors to get user data from Redux
const getUserData = (state: RootState) => ({
  userName: state.user.userName,
  email: state.user.email,
  baseUrl: state.user.baseUrl,
});

// Helper: Add common params (username, email) to request, and prepend baseUrl for relative paths
function addCommonParams(effect: any, userName: string, email: string, baseUrl: string) {
  // If URL is relative (starts with /), prepend baseUrl
  let url = effect.url.startsWith('http') ? effect.url : `${baseUrl}${effect.url}`;
  let jsonParams = effect.json || {};

  // Add username, email, and system to query params for non-OPTIONS requests
  if (effect.method !== 'OPTIONS') {
    const separator = url.includes('?') ? '&' : '?';
    url = `${url}${separator}UserName=${userName}`;
    if (email) {
      url = `${url}&Email=${email}`;
    }
    const system = getCurrentSystemKey();
    url = `${url}&system=${system}`;
  }

  // Add username to JSON body for non-GET requests
  if (effect.method !== 'GET' && !Array.isArray(jsonParams)) {
    jsonParams = {
      ...jsonParams,
      UserName: userName,
    };
  }

  return { url, jsonParams };
}

// Send HTTP request with Cognito authorization
function* cognitoRequestSender(action: any): Generator<any, void, any> {
  const { effect, commit, rollback } = action.http.cognito;
  // If Cognito cookies were cleared by another tab's sign-out, the library's
  // InMemoryTokenStore still holds a cached token. Detect the missing cookies
  // and force a full sign-out + redirect instead of letting the stale cache
  // through.
  const hasCookie = document.cookie.split(";").some((c) => c.trim().startsWith("CognitoIdentityServiceProvider"));
  if (!hasCookie) {
    if (isGohHost()) {
      window.location.href = buildPortalUrl();
    } else {
      redirectToLoginPage();
    }
    return;
  }
  const token: string = yield call(getAccessToken);
  // Get username, email, and baseUrl from Redux store
  const { userName, email, baseUrl }: { userName?: string; email?: string; baseUrl?: string } = yield select(getUserData);

  if (!token) {
    console.warn("[Saga] No valid token available for action:", action.type);
    if (isGohHost()) {
      window.location.href = buildPortalUrl();
    } else {
      redirectToLoginPage();
    }
    return;
  }

  const { url, jsonParams } = addCommonParams(effect, userName || '', email || '', baseUrl || '');

  try {
    const response: Response = yield call(fetch, url, {
      method: effect.method,
      headers: {
        Authorization: token,
        "Content-Type": "application/json",
        'X-Requested-With': 'XMLHttpRequest', // CSRF protection
        'X-MSA-Client': 'web-app', // Custom client identifier
        'X-MSA-Client-Version': '1.0.0', // Client version for tracking
        ...(effect.headers || {}),
      },
      body: effect.method !== 'GET' ? JSON.stringify(jsonParams) : null,
    });

    if (response.ok) {
      // Success
      const jsonResponse: string = yield call([response, 'text']);
      yield put({
        ...commit,
        statusCode: response.status,
        payload: JSON.parse(jsonResponse.replace(/\bNaN\b/g, "null")),
      });
    } else {
      // failure
      const jsonResponse: string = yield call([response, 'text']);
      yield put({
        ...rollback,
        statusCode: response.status,
        payload: (() => { try { return JSON.parse(jsonResponse); } catch { return { message: jsonResponse }; } })(),
      });
    }
  } catch (e: any) {
    console.error("[Saga] HTTP request error:", e);
    yield put(rollback);
  }
}
